// stores/cartStore.ts - Updated with proper variant handling

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, SelectedVariantPath } from '@/types/cart';
import { isSameVariantPath, generateVariantDisplayText } from '@/types/cart';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { generateId } from '@/utils/idGenerator';

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  isCartOpen: boolean;
  userId: string | null;
}

interface CartActions {
  addToCart: (item: Omit<CartItem, 'id' | 'totalPrice'>) => void;
  updateCartItem: (itemId: string, updates: Partial<CartItem>) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  getCartItem: (productId: string, variantPath?: SelectedVariantPath) => CartItem | undefined;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  setUserId: (userId: string | null) => void;
  clearCartOnLogout: () => void;
  migrateGuestCart: (newUserId: string) => void;
}

type CartStore = CartState & CartActions;

const calculateTotals = (items: CartItem[]) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.totalPrice, 0);
  return { totalItems, totalPrice };
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      totalItems: 0,
      totalPrice: 0,
      isCartOpen: false,
      userId: null,

      // Actions
      addToCart: (newItem) => {
        const id = generateId();
        const totalPrice = newItem.price * newItem.quantity;
        
        // Generate variant display text if variants are selected
        const variantDisplayText = newItem.selectedVariantPath 
          ? generateVariantDisplayText(newItem.selectedVariantPath)
          : undefined;
        
        const cartItem: CartItem = { 
          ...newItem, 
          id, 
          totalPrice,
          variantDisplayText 
        };

        set((state) => {
          // Check if item with same product and variant path exists
          const existingIndex = state.items.findIndex(
            (item) =>
              item.productId === newItem.productId &&
              isSameVariantPath(item.selectedVariantPath, newItem.selectedVariantPath)
          );

          let newItems: CartItem[];
          
          if (existingIndex >= 0) {
            // Update quantity if exists
            newItems = [...state.items];
            const existingItem = newItems[existingIndex];
            newItems[existingIndex] = {
              ...existingItem,
              quantity: existingItem.quantity + newItem.quantity,
              totalPrice: existingItem.price * (existingItem.quantity + newItem.quantity),
            };
            
            toast.success('Cart updated', {
              description: `${newItem.productName}${variantDisplayText ? ` (${variantDisplayText})` : ''} quantity increased`,
            });
          } else {
            newItems = [...state.items, cartItem];
            toast.success('Added to cart', {
              description: `${newItem.productName}${variantDisplayText ? ` - ${variantDisplayText}` : ''} has been added to your cart`,
            });
          }

          const { totalItems, totalPrice } = calculateTotals(newItems);
          return { items: newItems, totalItems, totalPrice };
        });
      },

      updateCartItem: (itemId, updates) => {
        set((state) => {
          const newItems = state.items.map((item) => {
            if (item.id === itemId) {
              const updatedItem = { ...item, ...updates };
              
              // Recalculate total price if quantity or price changed
              if (updates.quantity !== undefined || updates.price !== undefined) {
                updatedItem.totalPrice = updatedItem.price * updatedItem.quantity;
              }
              
              // Update variant display text if variant path changed
              if (updates.selectedVariantPath !== undefined) {
                updatedItem.variantDisplayText = generateVariantDisplayText(updates.selectedVariantPath);
              }
              
              return updatedItem;
            }
            return item;
          });

          const { totalItems, totalPrice } = calculateTotals(newItems);
          toast.success('Cart updated');
          return { items: newItems, totalItems, totalPrice };
        });
      },

      removeFromCart: (itemId) => {
        set((state) => {
          const removedItem = state.items.find(item => item.id === itemId);
          const newItems = state.items.filter((item) => item.id !== itemId);
          const { totalItems, totalPrice } = calculateTotals(newItems);
          
          if (removedItem) {
            toast.success('Item removed', {
              description: `${removedItem.productName}${removedItem.variantDisplayText ? ` (${removedItem.variantDisplayText})` : ''} removed from cart`,
            });
          }
          
          return { items: newItems, totalItems, totalPrice };
        });
      },

      clearCart: () => {
        set({ items: [], totalItems: 0, totalPrice: 0 });
        toast.success('Cart cleared');
      },

      getCartItem: (productId, variantPath) => {
        const { items } = get();
        return items.find(
          (item) =>
            item.productId === productId &&
            isSameVariantPath(item.selectedVariantPath, variantPath)
        );
      },

      openCart: () => set({ isCartOpen: true }),
      closeCart: () => set({ isCartOpen: false }),
      toggleCart: () => set((state) => ({ isCartOpen: !state.isCartOpen })),

      // User management
      setUserId: (userId) => {
        const currentUserId = get().userId;
        
        // If switching users, clear the cart
        if (currentUserId && userId && currentUserId !== userId) {
          set({ 
            items: [], 
            totalItems: 0, 
            totalPrice: 0, 
            userId,
            isCartOpen: false 
          });
        } else {
          set({ userId });
        }
      },

      clearCartOnLogout: () => {
        set({ 
          items: [], 
          totalItems: 0, 
          totalPrice: 0, 
          userId: null,
          isCartOpen: false 
        });
      },

      migrateGuestCart: (newUserId) => {
        const { userId } = get();
        
        // Only migrate if coming from guest (null userId) to authenticated
        if (userId === null) {
          set({ userId: newUserId });
        }
      },
    }),
    {
      name: 'auqli-cart-storage',
      partialize: (state) => ({
        items: state.items,
        totalItems: state.totalItems,
        totalPrice: state.totalPrice,
        userId: state.userId,
      }),
    }
  )
);

// Hook to sync cart with authentication state
export const useSyncCartWithAuth = (userId: string | null, isLoading: boolean) => {
  const { setUserId, clearCartOnLogout } = useCartStore();
  
  useEffect(() => {
    if (isLoading) return;
    
    if (userId) {
      setUserId(userId);
    } else {
      clearCartOnLogout();
    }
  }, [userId, isLoading, setUserId, clearCartOnLogout]);
};